<script lang="ts">
  /**
   * A page of ruled paper.
   *
   * Renders `$slots` lines ALWAYS, filling the empty ones with blanks. That is
   * not laziness — it is the method's core geometry (guarantee 1): a page is a
   * physical object with a fixed number of lines, and a struck-off task leaves
   * its line in place rather than closing the gap.
   *
   * Date markers are placed by comparing each task's `logical_day` with the
   * previous task's, so a marker renders immediately before the FIRST task of a
   * day and is anchored to that task's identity (§7.3).
   */
  import type { Page, Projection, Task } from '../types/generated';
  import { tasksOnPage } from '../storage/fold';
  import { reentryDepth } from '../utils/taskStats';
  import TaskLine from './TaskLine.svelte';

  interface Props {
    page: Page;
    /** Which half of the render model to draw. Default: the durable one. */
    projection: Projection;
    /** Task ids that are still unacknowledged predictions (§6.2). */
    pendingIds?: Set<string>;
    /** The page's focused task, from the `J`/`K` keyboard layer. */
    focusedTaskId?: string | null;
    /** Task ids whose confirmed slot differed from the prediction. */
    relayoutIds?: Set<string>;
    /** True for the writing page where new tasks are being added. */
    isFinalPage?: boolean;
    /**
     * Archived context (browse/graveyard): date markers always carry the year
     * (ARCHITECTURE.md §4 guarantee 5). Default false — the notebook view is
     * byte-identical.
     */
    archived?: boolean;
    /**
     * False in read-only views: lines are not tab-focusable at all
     * (STEP_8_SPEC.md Review Focus 4). Default true.
     */
    interactive?: boolean;
    /**
     * Browse search: ids of the tasks that match the current query, dimming
     * the rest. Null — the default — means no query: nothing is dimmed.
     */
    matchingIds?: Set<string> | null;
    /**
     * The working notebook's identity for the header title (ideas 4+18).
     * Optional: browse/graveyard hand none and their header is unchanged.
     */
    notebookName?: string;
    notebookTag?: string;
    /**
     * R2.4: the container's click-to-work handler. Absent (browse) a row
     * click does nothing — the PageFooter optional-callback pattern.
     */
    onActivateTask?: (taskId: string) => void;
    /**
     * R3.1: the page-turn direction, for the flip animation on the card
     * itself. Absent everywhere a mount is not a turn.
     */
    turn?: 'forward' | 'back' | null;
  }

  let {
    page,
    projection,
    pendingIds = new Set(),
    focusedTaskId = null,
    relayoutIds = new Set(),
    isFinalPage = false,
    archived = false,
    interactive = true,
    matchingIds = null,
    notebookName,
    notebookTag,
    onActivateTask,
    turn = null,
  }: Props = $props();

  /**
   * R2.5: the one row whose stats block is open — one per page, so another
   * row's stats opening closes this one's. Browse passes no handler and gets
   * no toggle (interactive is false there).
   */
  let openStatsId = $state<string | null>(null);

  const capacity = $derived(projection.notebook.page_capacity);
  const tasks = $derived<Task[]>(tasksOnPage(projection, page.id));
  const slots = $derived(Array.from({ length: capacity }, (_, i) => i));

  /** Child counts, so a re-entered task can show its `↳n`. */
  const childCounts = $derived(
    tasks.reduce<Record<string, number>>((acc, task) => {
      if (task.parent_task_id) {
        acc[task.parent_task_id] = (acc[task.parent_task_id] ?? 0) + 1;
      }
      return acc;
    }, {}),
  );

  /**
   * R4.8: each task's children — titles and the pages they landed on — for the
   * `↳` arrow's hover tooltip. Read from the WHOLE projection, not just this
   * page's tasks: a child lands on the writing page, which is usually a
   * different page than its parent. Ordered by page, then line, so the tooltip
   * is stable. Presentation only.
   */
  const childItems = $derived.by(() => {
    const grouped: Record<
      string,
      { title: string; pageNumber: number | undefined; line: number }[]
    > = {};
    for (const task of Object.values(projection.tasks)) {
      if (!task.parent_task_id) continue;
      (grouped[task.parent_task_id] ??= []).push({
        title: task.title,
        pageNumber: projection.pages[task.page_id]?.page_number,
        line: task.line_index,
      });
    }
    const byParent: Record<string, { title: string; pageNumber: number | undefined }[]> = {};
    for (const [parentId, list] of Object.entries(grouped)) {
      byParent[parentId] = list
        .sort((a, b) => (a.pageNumber ?? 0) - (b.pageNumber ?? 0) || a.line - b.line)
        .map(({ title, pageNumber }) => ({ title, pageNumber }));
    }
    return byParent;
  });

  /**
   * A marker renders on line 0 of the page, or wherever the logical day differs
   * from the PREVIOUS task's. Comparing to the previous task rather than
   * scanning for gaps is what anchors a marker to a task instead of a position.
   */
  const markerLines = $derived(
    new Set(
      tasks
        .map((task, index) => {
          const previous = tasks[index - 1];
          if (index > 0 && previous && previous.logical_day === task.logical_day) {
            return null;
          }
          return task.line_index;
        })
        .filter((line): line is number => line !== null),
    ),
  );

  const byLine = $derived(new Map(tasks.map((t) => [t.line_index, t])));
</script>

<section
  class="page-card"
  class:page-card--turn-forward={turn === 'forward'}
  class:page-card--turn-back={turn === 'back'}
  style="--slots: {capacity}"
  data-testid="page-card"
  data-page-number={page.page_number}
  data-slots={capacity}
  aria-label="Page {page.page_number}"
>
  <header class="page-header">
    {#if notebookName}
      <span class="page-title" data-testid="page-title">
        {notebookName}
        {#if notebookTag}<span class="page-title-tag">@{notebookTag}</span>{/if}
      </span>
    {/if}
    <span class="page-number">Page {page.page_number}</span>
    {#if isFinalPage}
      <span class="page-role">Final page — writing</span>
    {:else if page.status === 'DeadCompleted'}
      <span class="page-role">Complete</span>
    {:else if page.status === 'DeadDismissed'}
      <span class="page-role">Dismissed</span>
    {/if}
  </header>

  <div class="page-lines" role="listbox" aria-label="Tasks on page {page.page_number}">
    {#each slots as line (line)}
      {@const task = byLine.get(line)}
      {#if task}
        <TaskLine
          {task}
          pending={pendingIds.has(task.task_id)}
          showDateMarker={markerLines.has(line)}
          focused={focusedTaskId === task.task_id}
          relayout={relayoutIds.has(task.task_id)}
          childCount={childCounts[task.task_id] ?? 0}
          childItems={childItems[task.task_id] ?? []}
          forceYear={archived}
          focusable={interactive}
          matched={!matchingIds || matchingIds.has(task.task_id)}
          onActivate={onActivateTask ? () => onActivateTask(task.task_id) : undefined}
          statsOpen={openStatsId === task.task_id}
          onToggleStats={interactive
            ? () =>
                (openStatsId =
                  openStatsId === task.task_id ? null : task.task_id)
            : undefined}
          reentryCount={reentryDepth(Object.values(projection.tasks), task.task_id)}
        />
      {:else}
        <!-- An empty line still exists and still has its rule. -->
        <div class="line" data-empty="true" aria-hidden="true"></div>
      {/if}
    {/each}
  </div>
</section>